import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Instagram, Facebook, Youtube } from "lucide-react";
import { useAuth } from "@/context/Auth";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Logo from "../../assets/images/logo/Fayoz - gold1.png";

export default function Footer() {
  const { subscribeToNewsletter } = useAuth();
  const [email, setEmail] = useState("");
  const currentYear = new Date().getFullYear();
  const [showShop, setShowShop] = useState(false);
  const [showSupport, setShowSupport] = useState(false);

  const handleSubscribe = async () => {
    if (!email) {
      toast.error("Email is required.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    try {
      await subscribeToNewsletter(email);
      setEmail("");
    } catch {
      // Error already handled in context with toast
    }
  };

  return (
    <footer className="bg-primary text-white">
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center gap-2">
              <img
                src={Logo}
                style={{ width: "120px", height: "40px", align: "center" }}
              />
            </Link>
            <p className="text-gray-300 text-sm">
              Premium skin and hair care products for healthy radiant skin and
              ravishing hair. Tested and Trusted.
            </p>
            <div className="flex gap-4">
              <Link
                to="https://instagram.com/fayozskinandhair"
                className="text-gray-300 hover:text-secondary transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </Link>
              <Link
                to="#"
                className="text-gray-300 hover:text-secondary transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </Link>
              <Link
                to="https://x.com/FayozStar"
                className="text-gray-300 hover:text-secondary transition-colors"
              >
                <i className="fa-brands fa-x-twitter text-xl"></i>
              </Link>
              <Link
                to="#"
                className="text-gray-300 hover:text-secondary transition-colors"
              >
                <Youtube className="w-5 h-5" />
              </Link>
            </div>
          </div>

          {/* Shop */}
          <div>
            <div
              className="flex justify-between items-center md:block cursor-pointer"
              onClick={() => setShowShop(!showShop)}
            >
              <h3 className="font-semibold mb-4 text-secondary">Shop</h3>
              <span className="md:hidden text-gray-300 text-xl">
                {showShop ? "−" : "+"}
              </span>
            </div>
            <ul
              className={`space-y-2 text-sm ${
                showShop ? "block" : "hidden"
              } md:block`}
            >
              {[
                { label: "All Products", href: "/shop" },
                { label: "Cleansers", href: "/shop?search=cleansers" },
                { label: "Serums", href: "/shop?search=serums" },
                { label: "Moisturizers", href: "/shop?search=moisturizers" },
                { label: "Suncare", href: "/shop?search=suncare" },
                { label: "Gift Sets", href: "/shop?search=gift sets" },
              ].map((item, idx) => (
                <li key={idx}>
                  <Link
                    to={item.href}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <div
              className="flex justify-between items-center md:block cursor-pointer"
              onClick={() => setShowSupport(!showSupport)}
            >
              <h3 className="font-semibold mb-4 text-secondary">Support</h3>
              <span className="md:hidden text-gray-300 text-xl">
                {showSupport ? "−" : "+"}
              </span>
            </div>
            <ul
              className={`space-y-2 text-sm ${
                showSupport ? "block" : "hidden"
              } md:block`}
            >
              {[
                { label: "Contact Us", href: "/contact-us" },
                { label: "FAQ", href: "/faq" },
                // { label: "Shipping Info", href: "/shipping" },
                // { label: "Returns", href: "/returns" },
                // { label: "Size Guide", href: "/size-guide" },
                { label: "Track Order", href: "/track-order" },
              ].map((item, idx) => (
                <li key={idx}>
                  <Link
                    to={item.href}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="font-semibold mb-4 text-secondary">Stay Updated</h3>
            <p className="text-gray-300 text-sm mb-4">
              Get the latest skin and hair care tips and stay updated with
              exclusive offers.
            </p>
            <div className="space-y-3">
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
              <Button
                onClick={handleSubscribe}
                className="w-full bg-secondary hover:bg-secondary/90 text-primary font-medium"
              >
                Subscribe
              </Button>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              By subscribing, you agree to our Privacy Policy
            </p>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-300 text-sm">
            © {currentYear} Fayoz. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm">
            <Link
              to="/privacy-policy"
              className="text-gray-300 hover:text-white transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms-of-service"
              className="text-gray-300 hover:text-white transition-colors"
            >
              Terms of Service
            </Link>
            <Link
              to="/refund-policy"
              className="text-gray-300 hover:text-white transition-colors"
            >
              Refund Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
